import { Injectable, inject } from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { HttpClient } from '@angular/common/http'
import { RestClientList } from '@elderbyte/ngx-starter'
import { environment } from '../../../environments/environment'
import { combineLatest, Observable, ReplaySubject } from 'rxjs'
import { EntityServiceLink } from './entity-service-link'
import { EntitySpec } from './entity-spec'
import { first, map } from 'rxjs/operators'
import { Company } from '../companies/company'
import { CompanyService } from '../companies/company.service'

@Injectable({
  providedIn: 'root',
})
export class EntityLinkService extends RestClientList<EntitySpec, string> {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger('EntityLinkService')
  private readonly entityLinkTemplates$ = new ReplaySubject<Map<string, EntityServiceLink[]>>(1)
  private readonly companies$ = new ReplaySubject<Map<string, Company>>(1)
  private readonly companyService = inject(CompanyService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const http = inject(HttpClient)
    super(environment.apiPath + '/entity-links', http)
    this.reloadServiceLinkTemplates()
    this.reloadCompanies()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public getEntityServiceLink(
    entityType: string,
    entityId: string,
    service: string,
    company?: string
  ): Observable<EntityServiceLink> {
    return this.getLinksForEntity(entityType, entityId, company).pipe(
      map((links) => {
        return links.find((l) => l.service === service)
      })
    )
  }

  public getLinksForEntity(
    entityType: string,
    entityId: string,
    company?: string
  ): Observable<EntityServiceLink[]> {
    return combineLatest([this.companies$, this.entityLinkTemplates$]).pipe(
      first(),
      map(([companies, templates]) => {
        const cmp = companies.get(company)
        const context = this.buildContext(cmp, entityId)
        const services = templates.get(entityType) ?? []
        return services.map((s) => this.resolveEntityLink(s, context))
      })
    )
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private reloadServiceLinkTemplates(): void {
    this.findAll().subscribe({
      next: (links) => {
        const linksByEntity = new Map<string, EntityServiceLink[]>()
        links.forEach((l) => linksByEntity.set(l.type, l.services))
        this.entityLinkTemplates$.next(linksByEntity)
      },
      error: (err) => this.log.error('Failed to load entity link templates!', err),
    })
  }

  private reloadCompanies(): void {
    this.companyService.findAll().subscribe({
      next: (companies) => {
        const companyMap = new Map<string, Company>()
        companies.forEach((c) => companyMap.set(c.id, c))
        this.companies$.next(companyMap)
      },
      error: (err) => this.log.error('Failed to load companies!', err),
    })
  }

  private buildContext(company: Company, entityId: string): Map<string, string> {
    const context = new Map<string, string>()
    context.set('company', company?.id)
    context.set('entityId', entityId)
    context.set('storageSpace', company?.storageSpace)
    return context
  }

  private resolveEntityLink(
    link: EntityServiceLink,
    context: Map<string, string>
  ): EntityServiceLink {
    let resolvedTemplate = link.urlTemplate

    context.forEach((v, k) => {
      resolvedTemplate = resolvedTemplate.replace('{' + k + '}', v)
    })

    return {
      name: link.name,
      service: link.service,
      url: resolvedTemplate,
    }
  }
}
